import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { createPDF, brandedAutoTable, addFooter, addHeader } from "@/utils/pdfUtils";
import { 
  Zap, ArrowLeft, Calendar, BarChart3, FileSpreadsheet, 
  FileText, LogOut, GitCompare, CheckSquare
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamComparison() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchEvents();
  }, [navigate]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`, getAuthHeaders());
      // Only show events with responses
      const eventsWithResponses = response.data.filter(e => e.response_count > 0);
      setEvents(eventsWithResponses);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const toggleEventSelection = (eventId) => {
    setSelectedEvents(prev => {
      if (prev.includes(eventId)) {
        return prev.filter(id => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
  };

  const fetchComparisonData = async () => {
    if (selectedEvents.length < 2) {
      toast.error("Select at least 2 events to compare");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/reports/compare`,
        { event_ids: selectedEvents },
        getAuthHeaders()
      );
      setComparisonData(response.data);
    } catch (error) {
      toast.error("Failed to fetch comparison data");
    }
  };

  const exportToExcel = () => {
    if (!comparisonData) return;

    const data = comparisonData.events.map((e) => ({
      Event: e.name,
      Date: e.date,
      Country: e.country,
      Responses: e.response_count,
      "TeamPower Index": e.avg_teampower_index,
      Capability: e.avg_capability_score,
      Collaboration: e.avg_collaboration_score,
      Energy: e.avg_energy_score,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Team Comparison");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `TeamPower_Comparison.xlsx`);
    toast.success("Excel exported!");
  };

  const exportToPDF = () => {
    if (!comparisonData) return;

    const doc = createPDF();

    const contentY = addHeader(doc);

    doc.setFontSize(16);
    doc.setTextColor(236, 72, 153);
    doc.text("Team Comparison", 105, contentY + 2, { align: "center" });

    const tableData = comparisonData.events.map((e) => [
      e.name,
      e.date,
      e.response_count,
      e.avg_teampower_index,
      e.avg_capability_score,
      e.avg_collaboration_score,
      e.avg_energy_score,
    ]);

    brandedAutoTable(doc, {
      startY: contentY + 10,
      head: [["Event", "Date", "Responses", "TeamPower", "Capability", "Collaboration", "Energy"]],
      body: tableData,
      theme: "grid",
    });

    addFooter(doc);
    doc.save("TeamPower_Comparison.pdf");
    toast.success("PDF exported!");
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Prepare chart data
  const chartData = comparisonData?.events.map((e) => ({
    name: e.name.length > 15 ? e.name.substring(0, 15) + "..." : e.name,
    TeamPower: e.avg_teampower_index,
    Capability: e.avg_capability_score,
    Collaboration: e.avg_collaboration_score,
    Energy: e.avg_energy_score,
  })) || [];

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-64 bg-zinc-900/50 border-r border-zinc-800 p-6 hidden lg:block">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <p className="font-heading text-sm tracking-wide">TEAMPOWER</p>
            <p className="text-xs text-zinc-500">Admin</p>
          </div>
        </div>

        <nav className="space-y-2">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="sidebar-link w-full"
          >
            <Calendar className="w-5 h-5" />
            Events
          </button>
          <button
            onClick={() => navigate("/admin/reports")}
            className="sidebar-link w-full"
          >
            <BarChart3 className="w-5 h-5" />
            Reports
          </button>
          <button className="sidebar-link active w-full">
            <GitCompare className="w-5 h-5" />
            Team Comparison
          </button>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-zinc-400 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/dashboard")} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <span className="font-heading text-sm">TEAM COMPARISON</span>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/dashboard")} className="text-zinc-400 hover:text-white" title="Events">
              <Calendar className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/reports")} className="text-zinc-400 hover:text-white" title="Reports">
              <BarChart3 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-400 hover:text-white" title="Logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>

        <main className="p-6 md:p-8">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl tracking-wide">TEAM COMPARISON</h1>
              <p className="text-zinc-500">Select events to compare</p>
            </div>

            {comparisonData && (
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={exportToExcel}
                  className="bg-zinc-800 hover:bg-zinc-700 text-white"
                  data-testid="export-excel-button"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Excel
                </Button>
                <Button
                  variant="secondary"
                  onClick={exportToPDF}
                  className="bg-zinc-800 hover:bg-zinc-700 text-white"
                  data-testid="export-pdf-button"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </div>
            )}
          </div>

          {/* Event Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg">EVENT COMPARISON</h2>
              <Button
                onClick={fetchComparisonData}
                disabled={selectedEvents.length < 2}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="compare-button"
              >
                <GitCompare className="w-4 h-4 mr-2" />
                Compare ({selectedEvents.length})
              </Button>
            </div>

            {events.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No events with responses found</p>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedEvents.includes(event.id)
                        ? "border-orange-500 bg-orange-500/10"
                        : "border-zinc-800 hover:border-zinc-700"
                    }`}
                    onClick={() => toggleEventSelection(event.id)}
                    data-testid={`event-select-${event.id}`}
                  >
                    <Checkbox
                      checked={selectedEvents.includes(event.id)}
                      onCheckedChange={() => toggleEventSelection(event.id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-white">{event.name}</p>
                      <p className="text-sm text-zinc-500">{event.date}</p>
                      <p className="text-sm text-zinc-500">{event.response_count} responses</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Comparison Results */}
          {comparisonData && (
            <>
              {/* Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-8"
              >
                <h2 className="font-heading text-lg mb-6">COMPARISON RESULTS</h2>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={chartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis type="number" stroke="#71717A" domain={[0, 100]} />
                    <YAxis dataKey="name" type="category" stroke="#71717A" width={100} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#18181B",
                        border: "1px solid #27272A",
                        borderRadius: "8px",
                      }}
                    />
                    <Legend />
                    <Bar dataKey="TeamPower" fill="#F97316" />
                    <Bar dataKey="Capability" fill="#3B82F6" />
                    <Bar dataKey="Collaboration" fill="#A855F7" />
                    <Bar dataKey="Energy" fill="#F59E0B" />
                  </BarChart>
                </ResponsiveContainer>
              </motion.div>

              {/* Table */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden"
              >
                <div className="px-6 py-4 border-b border-zinc-800">
                  <h2 className="font-heading text-lg">Detailed Comparison</h2>
                </div>

                <div className="overflow-x-auto">
                  <table className="data-table" data-testid="comparison-table">
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Date</th>
                        <th>Responses</th>
                        <th>TeamPower</th>
                        <th>Capability</th>
                        <th>Collaboration</th>
                        <th>Energy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.events.map((event) => (
                        <tr key={event.id}>
                          <td className="font-medium">{event.name}</td>
                          <td className="text-zinc-400">{event.date}</td>
                          <td>{event.response_count}</td>
                          <td className="text-orange-400 font-bold">{event.avg_teampower_index}</td>
                          <td className="text-blue-400">{event.avg_capability_score}</td>
                          <td className="text-purple-400">{event.avg_collaboration_score}</td>
                          <td className="text-orange-400">{event.avg_energy_score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
